import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, ChevronDown, Loader2, Phone, X } from 'lucide-react';
import { IconBrandWhatsapp } from '@tabler/icons-react';
import '../../customer-experience/components/CxEmailTemplateModal.css';
import '../../customer-experience/components/CxEmailSendMailModal.css';
import './CustomersPhonesModal.css';
import {
  digitsOnlyPhone,
  isValidPhoneDigits,
  isValidWhatsAppDigits,
  maskPhoneBr,
  maskWhatsAppBr,
} from '../utils/customersPhoneMasks';

const MAIL_API = '/api/vc-usr/cx/config/mail/empresas';
const PHONES_API = '/api/vc-usr/cx/config/mail/empresas/phones';

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function draftSnapshot(row) {
  return JSON.stringify({
    phone: digitsOnlyPhone(row.phone, 11),
    whatsapp: digitsOnlyPhone(row.whatsapp, 11),
  });
}

function mapCompanyToDraft(company) {
  return {
    codigo: company.codigo,
    nome: company.nome,
    cnpj: company.cnpj,
    phone: digitsOnlyPhone(company.mail?.phone, 11),
    whatsapp: digitsOnlyPhone(company.mail?.whatsapp, 11),
    configured: Boolean(company.mail?.phonesConfigured),
  };
}

function rowValid(row) {
  const phone = digitsOnlyPhone(row.phone, 11);
  const wa = digitsOnlyPhone(row.whatsapp, 11);
  if (phone && !isValidPhoneDigits(phone)) return false;
  if (wa && !isValidWhatsAppDigits(wa)) return false;
  return true;
}

function PhonesCompanyRow({ row, baseline, expanded, onToggle, onChange }) {
  const isDirty = draftSnapshot(row) !== baseline;
  const phoneOk = !row.phone || isValidPhoneDigits(row.phone);
  const waOk = !row.whatsapp || isValidWhatsAppDigits(row.whatsapp);

  return (
    <article className={`cx-email-send-company${expanded ? ' cx-email-send-company--expanded' : ''}`}>
      <div className="cx-email-send-company-head">
        <span className="cx-email-send-company-code">{row.codigo}</span>
        <div className="cx-email-send-company-meta">
          <span className="cx-email-send-company-name" title={row.nome}>{row.nome}</span>
          {row.cnpj ? <span className="cx-email-send-company-cnpj">{row.cnpj}</span> : null}
        </div>
        <span
          className={`cx-email-send-company-badge${
            row.configured ? ' cx-email-send-company-badge--ok' : ' cx-email-send-company-badge--pending'
          }`}
        >
          {row.configured ? 'Configurado' : 'Pendente'}
        </span>
        <button
          type="button"
          className="cx-email-send-company-toggle"
          onClick={() => onToggle(row.codigo)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Recolher telefones' : 'Expandir telefones'}
        >
          <ChevronDown size={15} strokeWidth={2} aria-hidden />
        </button>
      </div>
      {expanded ? (
        <div className="cx-email-send-company-body">
          <div className="cx-email-send-fields customers-phones-fields">
            <label className="cx-email-send-field">
              <span className="cx-email-send-field-label">Telefone</span>
              <span className={`cx-email-send-field-input${!phoneOk ? ' is-invalid' : ''}`}>
                <Phone size={14} strokeWidth={2} aria-hidden />
                <input
                  type="tel"
                  inputMode="numeric"
                  value={maskPhoneBr(row.phone)}
                  onChange={(e) => onChange(row.codigo, { phone: digitsOnlyPhone(e.target.value, 11) })}
                  placeholder="(00)0000-0000"
                  autoComplete="tel"
                  maxLength={15}
                />
              </span>
              {!phoneOk ? (
                <span className="customers-phones-field-hint is-err">Informe no mínimo 10 dígitos.</span>
              ) : (
                <span className="customers-phones-field-hint">10 ou 11 dígitos</span>
              )}
            </label>
            <label className="cx-email-send-field">
              <span className="cx-email-send-field-label">WhatsApp</span>
              <span className={`cx-email-send-field-input${!waOk ? ' is-invalid' : ''}`}>
                <IconBrandWhatsapp size={14} stroke={2} aria-hidden />
                <input
                  type="tel"
                  inputMode="numeric"
                  value={maskWhatsAppBr(row.whatsapp)}
                  onChange={(e) => onChange(row.codigo, { whatsapp: digitsOnlyPhone(e.target.value, 11) })}
                  placeholder="(00)0000-0000"
                  autoComplete="tel"
                  maxLength={15}
                />
              </span>
              {!waOk ? (
                <span className="customers-phones-field-hint is-err">Informe no mínimo 10 dígitos.</span>
              ) : (
                <span className="customers-phones-field-hint">10 ou 11 dígitos</span>
              )}
            </label>
          </div>
          {isDirty ? (
            <p className="customers-phones-dirty-hint">Alterações pendentes de salvar.</p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default function CustomersPhonesModal({ open, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [baselines, setBaselines] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [expandedCodigos, setExpandedCodigos] = useState(() => new Set());

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(MAIL_API, { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);
      const list = (json.data?.companies || []).map(mapCompanyToDraft);
      const nextBaselines = {};
      for (const row of list) {
        nextBaselines[row.codigo] = draftSnapshot(row);
      }
      setRows(list);
      setBaselines(nextBaselines);
    } catch (e) {
      setError(e.message || 'Erro ao carregar empresas');
      setRows([]);
      setBaselines({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setExpandedCodigos(new Set());
    loadCompanies();
    return undefined;
  }, [open, loadCompanies]);

  const toggleExpanded = (codigo) => {
    setExpandedCodigos((prev) => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  };

  const dirtyRows = useMemo(
    () => rows.filter((row) => draftSnapshot(row) !== baselines[row.codigo]),
    [rows, baselines]
  );

  const canSave = dirtyRows.length > 0 && dirtyRows.every(rowValid);

  const patchRow = (codigo, patch) => {
    setRows((prev) =>
      prev.map((row) => (row.codigo === codigo ? { ...row, ...patch } : row))
    );
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(PHONES_API, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          companies: dirtyRows.map((row) => ({
            codigo: row.codigo,
            phone: digitsOnlyPhone(row.phone, 11),
            whatsapp: digitsOnlyPhone(row.whatsapp, 11),
          })),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);
      await loadCompanies();
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay cx-email-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div
        className="cx-email-modal cx-email-send-modal customers-phones-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customers-phones-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cx-email-send-header">
          <h2 id="customers-phones-modal-title" className="cx-email-send-title">
            Telefones por empresa
          </h2>
          <button
            type="button"
            className="cx-email-send-close"
            onClick={() => !saving && onClose?.()}
            aria-label="Fechar"
            disabled={saving}
          >
            <X size={18} strokeWidth={2} aria-hidden />
          </button>
        </div>

        <div className="cx-email-modal-body">
          <div className="cx-email-send-scroll">
            <p className="cx-email-send-intro">
              Configure telefone e WhatsApp de contato por empresa. Ambos aceitam 10 ou 11 dígitos.
            </p>

            {error ? (
              <div className="cx-email-send-result cx-email-send-result--err" role="alert">
                {error}
              </div>
            ) : null}

            {loading ? (
              <div className="cx-email-send-loading">
                <Loader2 size={22} className="cx-exp-spin" aria-hidden />
                Carregando empresas…
              </div>
            ) : rows.length === 0 ? (
              <div className="cx-email-send-empty">
                <Building2 size={24} strokeWidth={1.5} aria-hidden />
                Nenhuma empresa ativa encontrada.
              </div>
            ) : (
              <div className="cx-email-send-list">
                {rows.map((row) => (
                  <PhonesCompanyRow
                    key={row.codigo}
                    row={row}
                    baseline={baselines[row.codigo]}
                    expanded={expandedCodigos.has(row.codigo)}
                    onToggle={toggleExpanded}
                    onChange={patchRow}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <footer className="cx-email-modal-footer">
          <button
            type="button"
            className="cx-email-btn cx-email-btn--ghost cx-email-btn--link"
            disabled={saving}
            onClick={() => onClose?.()}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="cx-email-btn cx-email-btn--primary"
            disabled={saving || !canSave}
            onClick={handleSave}
          >
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
